// Sentry — optional client-side error tracking. Initialised at app
// bootstrap (main.ts) when `VITE_SENTRY_DSN` is set AND the host has
// installed `@sentry/vue`. Both checks must pass; otherwise the function
// is a no-op.
//
// Why we route the dynamic import through `Function(...)`: Vite/Rollup
// resolves every literal/template `import()` at build time and fails when
// the target package isn't installed. Wrapping the import in a runtime
// `Function` constructor keeps the bundler unaware of the dependency.
// The package is only resolved at runtime, where a missing module returns
// a `ModuleNotFoundError` we silently swallow.

import type { App } from 'vue'
import type { Router } from 'vue-router'

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
const ENV = (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? 'development'
const RELEASE = import.meta.env.VITE_SENTRY_RELEASE as string | undefined

let initialised = false

export async function initSentry(app: App, router: Router): Promise<void> {
  if (initialised) return
  if (!DSN) return
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let Sentry: any
  try {
    // The literal `'@sentry/vue'` is hidden behind a runtime Function so
    // the bundler doesn't try to resolve it at compile time.
    const dynImport = new Function('m', 'return import(m)') as (m: string) => Promise<unknown>
    Sentry = await dynImport('@sentry/vue')
  } catch {
    return
  }
  Sentry.init({
    app,
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: 0.05,
    sendDefaultPii: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    beforeSend(event: any) {
      try {
        const headers = (event.request?.headers ?? {}) as Record<string, string>
        for (const k of Object.keys(headers)) {
          if (k.toLowerCase() === 'authorization' || k.toLowerCase() === 'cookie') {
            headers[k] = '[redacted]'
          }
        }
        if (event.request) event.request.headers = headers
      } catch {
        /* noop */
      }
      return event
    },
  })
  initialised = true
}
